import React, { useMemo, useState } from 'react';
import { Activity, ChevronDown, ChevronUp, Clock, LogOut, Shield, User } from 'lucide-react';

const LABEL_FALLBACK = {
  user_finished: 'Candidate finished the interview',
  timer_expired: 'Interview time ran out',
  admin_ended: 'Ended by a company admin',
  duration_expired: 'Interview ended when the time limit was reached',
  client_unload: 'Candidate left the interview page and did not return',
  client_unload_after_degradation: 'Candidate left after the connection looked unstable',
  backgrounded_then_lost: 'Candidate switched away, then the session was lost',
  connection_lost_unconfirmed: 'Connection dropped — leave not confirmed',
  violation_terminated: 'Interview stopped due to a policy violation',
  unknown: 'How the interview ended could not be determined',
};

const EVENT_META = {
  CONNECT: {
    title: 'Joined the interview',
    detail: 'We recorded that the candidate opened the live interview page.',
    confidence: 'recorded',
    techMeaning: 'Browser connected to the live interview channel.',
  },
  RECONNECT: {
    title: 'Rejoined the interview',
    detail: 'We recorded that the candidate came back on a new live connection.',
    confidence: 'recorded',
    techMeaning: 'A new live connection opened after an earlier drop.',
  },
  CLIENT_HIDDEN: {
    title: 'Switched away from the interview',
    detail:
      'We recorded that the interview tab was no longer visible (another tab, app, or lock screen). This alone does not mean they left for good.',
    confidence: 'recorded',
    techMeaning: 'Page visibility became hidden. Not a full leave by itself.',
  },
  CLIENT_UNLOAD: {
    title: 'Left the interview page',
    detail:
      'Strong signal: the interview page went away and they did not return. Browsers do not tell us whether that was a tab close, refresh, or address-bar navigation.',
    confidence: 'strong',
    techMeaning:
      'Browser fired pagehide/unload. Proves the page went away; does not uniquely identify close vs refresh. Can also fire when the browser freezes the page in memory (bfcache) — we try to ignore that case.',
  },
  CLIENT_LEAVING_WS: {
    title: 'Left inside the interview app',
    detail: 'Strong signal: the candidate navigated away inside the interview app (in-app leave).',
    confidence: 'strong',
    techMeaning: 'Client intentionally closed the live channel with application code 4001.',
  },
  OFFLINE: {
    title: 'Live connection dropped',
    detail:
      'We recorded that the live connection closed. Alone this does not prove why — network drop, tab leave, or OS kill can all look similar.',
    confidence: 'recorded',
    techMeaning: 'WebSocket disconnected on the server.',
  },
};

const CONFIDENCE_BADGE = {
  strong: {
    label: 'Strong signal',
    className: 'text-emerald-700 bg-emerald-50 border-emerald-100',
  },
  recorded: {
    label: 'Recorded',
    className: 'text-slate-600 bg-slate-50 border-slate-200',
  },
  inferred: {
    label: 'Inferred',
    className: 'text-amber-800 bg-amber-50 border-amber-100',
  },
};

const CLOSE_CODE_MEANING = {
  1000: 'Normal close — connection ended cleanly',
  1001: 'Going away — browser left the page (tab close, navigation, or refresh)',
  1006: 'Abnormal close — connection dropped without a clean goodbye (often network)',
  4001: 'App leave — candidate left inside the interview screen (in-app navigation)',
};

function closeCodeMeaning(code) {
  if (code == null) return null;
  return CLOSE_CODE_MEANING[code] || `Close code ${code} (browser/server connection end reason)`;
}

function sameConnection(a, b) {
  if (!a?.connection_id || !b?.connection_id) return true;
  return a.connection_id === b.connection_id;
}

function isLeaveEvent(ev) {
  return (
    ev.event === 'CLIENT_UNLOAD' ||
    ev.event === 'CLIENT_LEAVING_WS' ||
    (ev.event === 'OFFLINE' && ev.close_code === 4001)
  );
}

function isGoingAwayOffline(ev) {
  return (
    ev &&
    ev.event === 'OFFLINE' &&
    (ev.close_code === 1001 || ev.close_code === 4001)
  );
}

function eventMeta(ev) {
  if (ev.event === 'OFFLINE' && (ev.close_code === 1001 || ev.close_code === 4001)) {
    return {
      title: 'Live connection closed (leaving browser)',
      detail:
        'We recorded a normal “going away” close. Often follows a page leave, but by itself it is not proof of tab close vs refresh.',
      confidence: 'recorded',
      techMeaning:
        ev.close_code === 4001
          ? CLOSE_CODE_MEANING[4001]
          : CLOSE_CODE_MEANING[1001],
    };
  }
  return (
    EVENT_META[ev.event] || {
      title: 'Activity recorded',
      detail: 'An interview activity event was recorded.',
      confidence: 'recorded',
      techMeaning: 'Unmapped presence event.',
    }
  );
}

/**
 * Human timeline: merge leave + connection-close in either order
 * (beacon unload and WS offline race either way).
 */
function buildStorySteps(events) {
  const steps = [];
  for (let i = 0; i < events.length; i += 1) {
    const ev = events[i];
    const next = events[i + 1];

    const leaveThenOffline = isLeaveEvent(ev) && isGoingAwayOffline(next) && sameConnection(ev, next);
    const offlineThenUnload =
      isGoingAwayOffline(ev) &&
      next &&
      (next.event === 'CLIENT_UNLOAD' || next.event === 'CLIENT_LEAVING_WS') &&
      sameConnection(ev, next);

    if (leaveThenOffline || offlineThenUnload) {
      const leaveEv = leaveThenOffline ? ev : next;
      const meta = eventMeta(leaveEv);
      steps.push({
        id: `${ev.id}-${next.id}`,
        when: formatTs(leaveEv.server_ts || ev.server_ts),
        title: meta.title,
        detail: meta.detail,
        confidence: meta.confidence || 'strong',
      });
      i += 1;
      continue;
    }

    const meta = eventMeta(ev);
    steps.push({
      id: ev.id,
      when: formatTs(ev.server_ts),
      title: meta.title,
      detail: meta.detail,
      confidence: meta.confidence || 'recorded',
    });
  }
  return steps;
}

function buildTechRows(events) {
  return [...events].reverse().map((ev) => {
    const meta = eventMeta(ev);
    return {
      id: ev.id,
      when: formatTs(ev.server_ts),
      code: ev.event,
      codeMeaning: meta.techMeaning,
      closeCode: ev.close_code,
      closeMeaning: closeCodeMeaning(ev.close_code),
      connectionId: ev.connection_id,
    };
  });
}

const VERDICT_CHIP = {
  good: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  fair: 'bg-amber-50 text-amber-900 border-amber-200',
  attention: 'bg-rose-50 text-rose-900 border-rose-200',
  neutral: 'bg-slate-50 text-slate-700 border-slate-200',
};

function verdictKey(reason) {
  if (
    reason === 'client_unload' ||
    reason === 'user_finished' ||
    reason === 'timer_expired' ||
    reason === 'admin_ended'
  ) {
    return 'good';
  }
  if (reason === 'client_unload_after_degradation' || reason === 'backgrounded_then_lost') {
    return 'fair';
  }
  if (reason === 'connection_lost_unconfirmed' || reason === 'unknown' || reason === 'violation_terminated') {
    return 'attention';
  }
  return 'neutral';
}

function formatTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

function endedByLabel(value) {
  if (!value) return 'Not recorded';
  if (value === 'candidate') return 'The candidate';
  if (value === 'admin') return 'A company admin';
  if (value === 'system') return 'The system';
  return value;
}

/**
 * True only when this session collected Phase-1 exit/presence data.
 * Old reports (pre feature) have none of these — hide the section entirely.
 * Use persisted session fields + presence rows only; ignore inferred exit_summary
 * end_reason (legacy completed sessions often get a soft "unknown" at read time).
 */
export function hasSessionExitEvidence(sessionData) {
  if (!sessionData?.id) return false;
  const exit = sessionData.exit_summary || null;
  const events = Array.isArray(exit?.presence_events) ? exit.presence_events : [];
  if (events.length > 0) return true;
  if (sessionData.end_reason) return true;
  if (sessionData.last_client_event) return true;
  if (sessionData.disconnect_at) return true;
  return false;
}

/**
 * Candidate exit & connection — styled to match ViewResult / TelemetryChart sections.
 * Hidden for legacy reports with no exit/presence data (backward compatible).
 */
export function SessionIntegrityPanel({ sessionData }) {
  const [openStory, setOpenStory] = useState(true);
  const [openTech, setOpenTech] = useState(false);

  const exit = sessionData?.exit_summary || null;
  const events = Array.isArray(exit?.presence_events) ? exit.presence_events : [];
  const endReason = exit?.end_reason || sessionData?.end_reason || null;
  const label =
    exit?.label ||
    LABEL_FALLBACK[endReason] ||
    (sessionData?.status === 'in_progress' ? 'Interview still in progress' : 'Exit reason not recorded');
  const summary =
    exit?.summary ||
    (endReason
      ? LABEL_FALLBACK[endReason]
      : 'We do not have enough activity history for this interview yet.');
  const supportAction = exit?.support_action || '';
  const provisional = Boolean(exit?.provisional);
  const verdict = verdictKey(endReason);

  const lastSeen = exit?.last_seen_at || sessionData?.last_seen_at;
  const disconnectAt = exit?.disconnect_at || sessionData?.disconnect_at;

  const storySteps = useMemo(() => buildStorySteps(events), [events]);
  const techRows = useMemo(() => buildTechRows(events), [events]);

  if (!hasSessionExitEvidence(sessionData)) return null;

  const kpis = [
    {
      label: 'Last activity',
      value: formatTs(lastSeen),
      hint: 'Last time we heard from the candidate',
      icon: <Clock className="w-4 h-4" />,
      color: 'from-cyan-600 to-sky-500',
    },
    {
      label: 'Left / disconnected',
      value: formatTs(disconnectAt),
      hint: 'When the leave or disconnect was recorded',
      icon: <LogOut className="w-4 h-4" />,
      color: 'from-indigo-600 to-indigo-400',
    },
    {
      label: 'Who ended it',
      value: endedByLabel(exit?.ended_by || sessionData?.ended_by),
      hint: 'Candidate, admin, or system',
      icon: <User className="w-4 h-4" />,
      color: 'from-slate-600 to-slate-400',
    },
  ];

  return (
    <div className="w-full rounded-xl p-4 sm:p-5 border border-cyan-100 bg-gradient-to-br from-cyan-50/30 to-white font-poppins transition-all pdf-page-break-inside-avoid shadow-sm print:shadow-none">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-2.5 rounded-xl shadow-md shrink-0">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900 leading-tight">
              Candidate exit &amp; connection
            </h2>
            <p className="text-slate-500 text-xs mt-0.5 font-medium">
              Whether they left the page, lost connection, or finished normally — best-effort browser
              and server evidence (not close vs refresh).
            </p>
          </div>
        </div>
        {provisional && (
          <span className="text-[10px] font-semibold text-slate-500 bg-slate-50 px-2.5 py-1 rounded-full border border-slate-100 tracking-wide">
            Still updating
          </span>
        )}
      </div>

      <div className={`mb-4 px-3.5 py-2.5 rounded-lg border text-sm font-medium ${VERDICT_CHIP[verdict]}`}>
        <div className="font-semibold text-[13px] leading-snug">{label}</div>
        <p className="text-xs font-medium opacity-80 mt-1 leading-relaxed">{summary}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {kpis.map((kpi) => (
          <div
            key={kpi.label}
            className="flex flex-col p-3.5 bg-white border border-slate-100 rounded-xl shadow-sm"
          >
            <div className="flex items-start gap-2.5">
              <div className={`p-1.5 rounded-md bg-gradient-to-br ${kpi.color} text-white shrink-0`}>
                {kpi.icon}
              </div>
              <div className="min-w-0 flex-1">
                <span className="block text-[11px] font-semibold text-slate-500 leading-snug">
                  {kpi.label}
                </span>
              </div>
            </div>
            <div className="mt-auto pt-2">
              <div className="text-sm font-bold text-slate-900 leading-snug">{kpi.value}</div>
              <p className="text-[10px] text-slate-500 leading-snug mt-1">{kpi.hint}</p>
            </div>
          </div>
        ))}
      </div>

      {supportAction ? (
        <div className="mb-4 px-3.5 py-2.5 rounded-lg border border-slate-100 bg-white shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-3.5 h-3.5 text-cyan-600 shrink-0" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              What this means for support
            </span>
          </div>
          <p className="text-xs font-medium text-slate-700 leading-relaxed">{supportAction}</p>
        </div>
      ) : null}

      {storySteps.length > 0 && (
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <button
            type="button"
            onClick={() => setOpenStory((v) => !v)}
            className="w-full flex items-center justify-between gap-2 text-left"
          >
            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
              What happened, step by step
            </h4>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400">
              {storySteps.length} steps
              {openStory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </span>
          </button>

          {openStory && (
            <ol className="mt-3 space-y-0">
              {storySteps.map((step, idx) => (
                <li key={step.id} className="relative flex gap-3 pb-3.5 last:pb-0">
                  <div className="flex flex-col items-center">
                    <span className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 bg-cyan-500 shadow-sm" />
                    {idx < storySteps.length - 1 && (
                      <span className="w-px flex-1 bg-slate-200 mt-1" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold text-slate-400">{step.when}</span>
                      {(() => {
                        const badge = CONFIDENCE_BADGE[step.confidence] || CONFIDENCE_BADGE.recorded;
                        return (
                          <span
                            className={`text-[9px] font-semibold border px-1.5 py-0.5 rounded-full ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">{step.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{step.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}

          <div className="mt-3 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setOpenTech((v) => !v)}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-slate-600"
            >
              {openTech ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Technical details (with meanings)
            </button>
            {openTech && (
              <div className="mt-3 space-y-2.5">
                {techRows.map((row) => (
                  <div
                    key={`tech-${row.id}`}
                    className="rounded-lg border border-slate-100 bg-slate-50/90 px-3.5 py-3"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-[10px] font-semibold text-slate-400">{row.when}</div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <code className="text-[11px] font-mono font-semibold text-slate-800 bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                            {row.code}
                          </code>
                          {row.closeCode != null && (
                            <code className="text-[11px] font-mono text-slate-600 bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                              close {row.closeCode}
                            </code>
                          )}
                        </div>
                      </div>
                      {row.connectionId && (
                        <span className="text-[10px] font-mono text-slate-400">
                          conn {String(row.connectionId).slice(0, 8)}…
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-slate-700 leading-relaxed">
                      <span className="font-semibold text-slate-500">Meaning: </span>
                      {row.codeMeaning}
                    </p>
                    {row.closeMeaning && (
                      <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                        <span className="font-semibold text-slate-500">Close code meaning: </span>
                        {row.closeMeaning}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 pt-3 flex flex-wrap items-center justify-end gap-2 border-t border-slate-100">
        <div className="flex items-center gap-2 px-2.5 py-1 bg-slate-50 rounded-lg border border-slate-100">
          <Shield className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
          <span className="text-[9px] font-semibold text-slate-500 tracking-wide">
            Captured automatically during the interview
          </span>
        </div>
      </div>
    </div>
  );
}

export default SessionIntegrityPanel;
